import { usePartnerConfig } from '@hooks/usePartnerConfig';
import { useCommonLabels } from '@hooks/useCommonLabels';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Context } from '../../../context/context';
import { IState } from '../../../context/stateModels';
import { StepperBarLabels } from '../../../context/labelContext';
import { macroStepList, StepToMacroStepMap, MacroStepKey, getMacroFillPercent } from '../../../utils/StepsMap';
import styles from './StepBar.module.scss';

export type MacroProgress = {
  order: number;
  title?: string;
  subtitle?: string;
  fillPercent: number;
};

const getMacroLabel = (key: MacroStepKey, field: 'Title' | 'Subtitle', labels?: StepperBarLabels) =>
  labels?.[`${key}${field}` as keyof StepperBarLabels];

export const getMacroProgress = (actualStep: string, labels?: StepperBarLabels): MacroProgress | null => {
  // The stepper always follows the real actualStep, going back included (#26994 monotonic guard
  // removed on Sofia's request): the bar must show the step the user is actually on.
  const currentMacroKey = StepToMacroStepMap[actualStep];
  const currentMacro = macroStepList.find(macro => macro.key === currentMacroKey);
  if (!currentMacro) {
    return null;
  }

  const fillPercent = getMacroFillPercent(actualStep, currentMacro.key);

  return {
    order: currentMacro.order,
    title: getMacroLabel(currentMacro.key, 'Title', labels),
    subtitle: getMacroLabel(currentMacro.key, 'Subtitle', labels),
    fillPercent
  };
};

const StepBar = () => {
  const { state } = useContext<IState>(Context);
  const { partnerConfig } = usePartnerConfig();
  const { stepperBar } = useCommonLabels();
  const [mounted, setMounted] = useState(false);

  const partnerClass = useMemo(() => (partnerConfig?.partnerName ? styles.partnerStyles : ''), [partnerConfig]);

  const macroProgress = useMemo(
    () => getMacroProgress(state.stepManager?.actualStep, stepperBar),
    [state.stepManager?.actualStep, stepperBar]
  );

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted || !macroProgress) {
    // SSR renders con lo stepManager di default (lo stesso browser-only session/devUtils di
    // StepperSidebar non e' disponibile lato server): dipingere i dati reali solo dopo il mount
    // evita il mismatch di hydration React 17 che aggiorna i testi ma non lo style dei segmenti
    // al primo hydrate pass (vedi stesso pattern in StepperSidebar.tsx).
    return null;
  }

  return (
    <div className={styles.stepBarWrapper}>
      <div className={styles.segments}>
        {macroStepList.map(macro => {
          const isCompleted = macro.order < macroProgress.order;
          const isCurrent = macro.order === macroProgress.order;
          let fillWidth = 0;
          if (isCompleted) {
            fillWidth = 100;
          } else if (isCurrent) {
            fillWidth = macroProgress.fillPercent;
          }
          return (
            <div key={macro.key} className={styles.segment}>
              <div className={`${styles.segmentFill} ${partnerClass}`} style={{ width: `${fillWidth}%` }} />
            </div>
          );
        })}
      </div>
      {/*
        #26994: il testo sta SOTTO la barra, e titolo, descrizione e contatore stanno sulla
        stessa riga (mock stepper_mobile.png). La descrizione e' l'unica parte che va a capo,
        percio' e' l'unica a cui e' concesso restringersi.
      */}
      <div className={styles.header}>
        <span className={styles.title}>{macroProgress.title}</span>
        <span className={styles.subtitle}>{macroProgress.subtitle}</span>
        <span className={styles.counter}>{`${macroProgress.order}/${macroStepList.length}`}</span>
      </div>
    </div>
  );
};

export default StepBar;
